import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import AuthGate from './components/AuthGate.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { ToastProvider } from './components/ToastCenter.jsx';
import { FEATURES } from './config/features.js';

// Lazy-load pages for faster initial bundle
const Login = lazy(() => import('./pages/Login.jsx'));
const Signup = lazy(() => import('./pages/Signup.jsx'));
// Self-service password recovery. Added 2026-09-24 — the Login page had a
// dead href="#" "Forgot?" link and no page behind it.
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx'));
// Netcom staff self-registration (demo access). Distinct from /signup,
// which is the customer ACCESS REQUEST and provisions nothing.
const StaffSignup = lazy(() => import('./pages/StaffSignup.jsx'));
const SsoCallback = lazy(() => import('./pages/SsoCallback.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Terminals = lazy(() => import('./pages/Terminals.jsx'));
const ServiceLines = lazy(() => import('./pages/ServiceLines.jsx'));
const Telemetry = lazy(() => import('./pages/Telemetry.jsx'));
const Billing = lazy(() => import('./pages/Billing.jsx'));
const Requests = lazy(() => import('./pages/Requests.jsx'));
const RequestNew = lazy(() => import('./pages/RequestNew.jsx'));
const Support = lazy(() => import('./pages/Support.jsx'));
const Alerts = lazy(() => import('./pages/Alerts.jsx'));
const AuditLog = lazy(() => import('./pages/AuditLog.jsx'));
const Network = lazy(() => import('./pages/Network.jsx'));
const Groups = lazy(() => import('./pages/Groups.jsx'));
const SlaPerformance = lazy(() => import('./pages/SlaPerformance.jsx'));
const Trends = lazy(() => import('./pages/Trends.jsx'));
const Firmware = lazy(() => import('./pages/Firmware.jsx'));
const Thresholds = lazy(() => import('./pages/Thresholds.jsx'));
const Maintenance = lazy(() => import('./pages/Maintenance.jsx'));
const IndustriesUtility = lazy(() => import('./pages/IndustriesUtility.jsx'));
const AccountExport = lazy(() => import('./pages/AccountExport.jsx'));
const SiteDetail = lazy(() => import('./pages/SiteDetail.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
// 2026-06-11 honesty pass: Team, SpeedTest, Optimizer, Pay, Dashboards,
// AdminBilling, AdminApiConsole, AdminHealth removed from routes (files kept —
// restore a route only when its real backend exists). ApiAccess + SSO/SCIM
// settings are gated behind FEATURES flags. See src/config/features.js.
const ApiAccess = lazy(() => import('./pages/ApiAccess.jsx'));
const ServiceLineDetail = lazy(() => import('./pages/ServiceLineDetail.jsx'));
const AddKit = lazy(() => import('./pages/AddKit.jsx'));
const AdminLeads = lazy(() => import('./pages/AdminLeads.jsx'));
const AdminAccounts = lazy(() => import('./pages/AdminAccounts.jsx'));
const AdminDishes = lazy(() => import('./pages/AdminDishes.jsx'));
const AdminSims = lazy(() => import('./pages/AdminSims.jsx'));
const AdminCustomer = lazy(() => import('./pages/AdminCustomer.jsx'));
const AdminUsers = lazy(() => import('./pages/AdminUsers.jsx'));
const AdminAudit = lazy(() => import('./pages/AdminAudit.jsx'));
const AdminBulkImport = lazy(() => import('./pages/AdminBulkImport.jsx'));
const AdminForceCreate = lazy(() => import('./pages/AdminForceCreate.jsx'));
const AdminStarlinkCreds = lazy(() => import('./pages/AdminStarlinkCreds.jsx'));
const Glossary = lazy(() => import('./pages/Glossary.jsx'));
const SsoSettings = lazy(() => import('./pages/SsoSettings.jsx'));
const ScimSettings = lazy(() => import('./pages/ScimSettings.jsx'));
const StatusPage = lazy(() => import('./pages/StatusPage.jsx'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings.jsx'));
const Onboarding = lazy(() => import('./pages/Onboarding.jsx'));
const Topology = lazy(() => import('./pages/Topology.jsx'));
const Trust = lazy(() => import('./pages/Trust.jsx'));
const TrustSubProcessors = lazy(() => import('./pages/TrustSubProcessors.jsx'));
const TrustResidency = lazy(() => import('./pages/TrustResidency.jsx'));
const TrustCertifications = lazy(() => import('./pages/TrustCertifications.jsx'));
const TrustSecurity = lazy(() => import('./pages/TrustSecurity.jsx'));
const Security = lazy(() => import('./pages/Security.jsx'));
// ComingSoon route now redirects to /login — per Dillon 2026-05-29 the public
// landing should BE the actual login experience (email + password right there
// in a larger, more enterprise-branded layout), not a separate marketing page.
// Kept lazy import in case we revive a marketing landing later.
const ComingSoon = lazy(() => import('./pages/ComingSoon.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));
// Netcom Connect SIM — white-labeled KORE OmniSIM Reach
const Sims = lazy(() => import('./pages/Sims.jsx'));
const SimDetail = lazy(() => import('./pages/SimDetail.jsx'));
// Field Services — Amazon Air client portal
const AmazonAirDashboard = lazy(() => import('./pages/FieldServices/AmazonAirDashboard.jsx'));
const AmazonAirSites = lazy(() => import('./pages/FieldServices/AmazonAirSites.jsx'));
const AmazonAirSurveyDetail = lazy(() => import('./pages/FieldServices/AmazonAirSurveyDetail.jsx'));
const AmazonAirWorkOrders = lazy(() => import('./pages/FieldServices/AmazonAirWorkOrders.jsx'));
const AmazonAirRequest = lazy(() => import('./pages/FieldServices/AmazonAirRequest.jsx'));
const AmazonAirCoverage = lazy(() => import('./pages/FieldServices/AmazonAirCoverage.jsx'));
const AmazonAirSitePrep = lazy(() => import('./pages/FieldServices/AmazonAirSitePrep.jsx'));

function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-slate-400 text-sm">Loading…</div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary scope="routes">
      <ToastProvider>
      <Suspense fallback={<PageLoading />}>
        <Routes>
        {/* Public routes — sign in / sign up / public status */}
        <Route path="/coming-soon" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/team-signup" element={<StaffSignup />} />
        <Route path="/auth/callback" element={<SsoCallback />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/industries/utility" element={<IndustriesUtility />} />
        <Route path="/trust/sub-processors" element={<TrustSubProcessors />} />
        <Route path="/trust/data-residency" element={<TrustResidency />} />
        <Route path="/trust/certifications" element={<TrustCertifications />} />
        <Route path="/trust/security" element={<TrustSecurity />} />

        {/* Protected routes — wrapped in AuthGate */}
        <Route element={<AuthGate><Layout /></AuthGate>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/terminals" element={<Terminals />} />
          <Route path="/terminals/:deviceId" element={<Telemetry />} />
          {/* Netcom Connect SIM (KORE OmniSIM Reach). Backend Lambda gates on
              'sim' product enablement via assertProductScope — the route
              itself is unconditional so deep links from email/Slack work
              even before session.products is hydrated. */}
          <Route path="/sims" element={<Sims />} />
          <Route path="/sims/:iccid" element={<SimDetail />} />
          <Route path="/service-lines" element={<ServiceLines />} />
          <Route path="/service-lines/:id" element={<ServiceLineDetail />} />
          <Route path="/add-kit" element={<AddKit />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/requests" element={<Requests />} />
          <Route path="/requests/new" element={<RequestNew />} />
          <Route path="/network" element={<Network />} />
          {FEATURES.FLEET_GROUPS && <Route path="/groups" element={<Groups />} />}
          <Route path="/alerts" element={<Alerts />} />
          {FEATURES.ENTERPRISE_ANALYTICS && <Route path="/sla" element={<SlaPerformance />} />}
          {FEATURES.ENTERPRISE_ANALYTICS && <Route path="/trends" element={<Trends />} />}
          {FEATURES.FIRMWARE_INTELLIGENCE && <Route path="/firmware" element={<Firmware />} />}
          {FEATURES.ALERT_RULES && <Route path="/thresholds" element={<Thresholds />} />}
          {FEATURES.MAINTENANCE_WINDOWS && <Route path="/maintenance" element={<Maintenance />} />}
          <Route path="/account/export" element={<AccountExport />} />
          <Route path="/sites/:siteName" element={<SiteDetail />} />
          {/* Netcom-staff god-mode. The Lambda still enforces the
              netcom-admin group on every request; the SPA routes are
              available to everyone but show 403-ish UI without the
              group. Sidebar hides these from non-admins. */}
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/accounts" element={<AdminAccounts />} />
          <Route path="/admin/dishes" element={<AdminDishes />} />
          <Route path="/admin/sims" element={<AdminSims />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/customer/:username" element={<AdminCustomer />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
          <Route path="/admin/bulk-import" element={<AdminBulkImport />} />
          <Route path="/admin/force-create" element={<AdminForceCreate />} />
          <Route path="/admin/starlink-credentials" element={<AdminStarlinkCreds />} />
          <Route path="/glossary" element={<Glossary />} />
          {/* Flag-gated: settings/token pages stay hidden until their
              backends are real (src/config/features.js). */}
          {FEATURES.SSO_SETTINGS && <Route path="/settings/sso" element={<SsoSettings />} />}
          {FEATURES.SCIM_SETTINGS && <Route path="/settings/scim" element={<ScimSettings />} />}
          {FEATURES.API_ACCESS && <Route path="/api-access" element={<ApiAccess />} />}
          <Route path="/settings/notifications" element={<NotificationSettings />} />
          <Route path="/welcome" element={<Onboarding />} />
          <Route path="/terminals/:deviceId/topology" element={<Topology />} />
          {FEATURES.ENTERPRISE_ANALYTICS && <Route path="/reports" element={<Reports />} />}
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/security" element={<Security />} />
          <Route path="/support" element={<Support />} />
          {/* Field Services — Amazon Air. Product-gated to 'field-services';
              routes unconditional so deep links work regardless of session hydration. */}
          <Route path="/field-services/amazon-air" element={<AmazonAirDashboard />} />
          <Route path="/field-services/amazon-air/sites" element={<AmazonAirSites />} />
          <Route path="/field-services/amazon-air/sites/:siteId" element={<AmazonAirSurveyDetail />} />
          <Route path="/field-services/amazon-air/work-orders" element={<AmazonAirWorkOrders />} />
          <Route path="/field-services/amazon-air/request" element={<AmazonAirRequest />} />
          <Route path="/field-services/amazon-air/coverage" element={<AmazonAirCoverage />} />
          <Route path="/field-services/amazon-air/prep" element={<AmazonAirSitePrep />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ToastProvider>
    </ErrorBoundary>
  );
}
